import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableSlotGroup, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { HeroSolidBGView, type HeroSolidBGContentProps } from './HeroSolidBG';

export function AmenitiesHero(props: HeroSolidBGContentProps) {
  const bgDefinition = contentSchema.slotGroups!['amenitieshero.bg_image'];
  const ctaDefinition = contentSchema.slotGroups!['amenitieshero.cta_link'];
  return (
    <>
      <HeroSolidBGView {...props} prefix="amenitieshero" bgDefinition={bgDefinition} />

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent path="amenitieshero.title" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableToken path="amenitieshero.subtitle" type="richtext" label="Subtitle" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <RichTextContent path="amenitieshero.blurb" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="amenitieshero.bg_image" definition={bgDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="amenitieshero.cta_link" definition={ctaDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesHero',
  displayName: 'Amenities Hero',
  tokens: {
    'amenitieshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Amenities</p>',
    },
    'amenitieshero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Apartment Amenities in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'amenitieshero.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultField: 'property_amenities_blurb',
    },
  },
  slotGroups: {
    'amenitieshero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Community amenities',
        },
      },
      generator: {
        prompt:
          'Pick the hero background photo for the Amenities page: a strong, high-quality photo of a shared community amenity this property actually has (for example a pool, clubhouse, fitness center, or outdoor amenity space). Write alt text describing what the photo shows.',
        context:
          'Full-bleed background hero at the top of the Amenities page, behind a semi-opaque primary-color overlay with centered white headline text. Prefer staged or professional photos; skip blurry, dark, cluttered, or unappealing shots. Priority: relevance to amenities first, image quality second, uniqueness third. Each inner page (Floor Plans, Amenities, Gallery, Virtual Tour, Neighborhood, Contact Us) has its own hero — do not reuse an image already used as another page hero or the homepage hero unless no other suitable image exists.',
      },
    },
    'amenitieshero.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(AmenitiesHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesHero;
